import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useApiErrorHandler } from "../lib/session";
import type { ArchiveCard, PlaceArchive as PlaceArchiveData } from "../lib/types";
import { Notice, ProgressPanel, Spinner, categoryLabel, placeTypeLabel } from "../components/bits";

/**
 * The screen used at home in February. It is the reason the app stores anything at
 * all, so it shows everything a visit contained rather than summarising it: the
 * card as it was worded that day, the photos, the notes, the recordings.
 */
export default function PlaceArchive() {
  const { placeId } = useParams();
  const navigate = useNavigate();
  const handleError = useApiErrorHandler();
  const [data, setData] = useState<PlaceArchiveData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api
      .place(Number(placeId))
      .then((result) => active && setData(result))
      .catch((err) => active && setError(handleError(err)));
    return () => {
      active = false;
    };
  }, [placeId, handleError]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  if (error) {
    return (
      <div className="screen screen--tabbed">
        <BackLink onClick={() => navigate(-1)} />
        <Notice tone="error">{error}</Notice>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="screen screen--tabbed">
        <Spinner />
      </div>
    );
  }

  const { place, visits } = data;
  const totalPoints = visits.reduce((sum, v) => sum + v.points, 0);
  const totalPhotos = visits.reduce(
    (sum, v) => sum + v.cards.reduce((n, c) => n + c.evidence.filter((e) => e.kind === "photo").length, 0),
    0,
  );

  return (
    <div className="screen screen--tabbed">
      <BackLink onClick={() => navigate(-1)} />

      <ProgressPanel
        name={place.name}
        earned={place.earned_points}
        target={place.target_points}
        baseTarget={place.starting_target}
        conquered={place.conquered}
        status={place.status}
      />

      {place.visited ? (
        <p className="tiny" style={{ margin: "12px 2px 0", color: "var(--visit)", fontWeight: 700 }}>
          Besucht am {new Date(place.visited_at!).toLocaleDateString("de-DE")} — du warst
          wirklich hier.
        </p>
      ) : null}

      <p className="tiny muted" style={{ margin: "12px 2px 0" }}>
        {placeTypeLabel(place.place_type)}
        {place.country_code ? ` · ${place.country_code}` : ""} · zuerst gesehen am{" "}
        {new Date(place.first_seen_at).toLocaleDateString("de-DE")} · {visits.length}{" "}
        {visits.length === 1 ? "Besuch" : "Besuche"} · {totalPhotos}{" "}
        {totalPhotos === 1 ? "Foto" : "Fotos"} · {totalPoints} Punkte verdient
      </p>

      {place.target_inflation > 0 ? (
        <p className="notice" style={{ marginTop: 14 }}>
          Dieser Ort begann bei {place.starting_target} Punkten und verlangte am Ende{" "}
          {place.target_points}.
          {place.discard_count > 0 ? (
            <>
              {" "}
              Du hast hier {place.discard_count}{" "}
              {place.discard_count === 1 ? "Karte" : "Karten"} abgeworfen.
            </>
          ) : null}
          {place.challenge_failures > 0 ? (
            <>
              {" "}
              {place.challenge_failures === 1
                ? "Eine Herausforderung ist danebengegangen"
                : `${place.challenge_failures} Herausforderungen sind danebengegangen`}{" "}
              (+{place.challenge_failure_penalty} Ziel).
            </>
          ) : null}
        </p>
      ) : null}

      {place.category_veto ? (
        <p className="notice" style={{ marginTop: 14 }}>
          {categoryLabel(place.category_veto)} hast du hier gestrichen
          {place.category_veto_penalty ? ` (+${place.category_veto_penalty} Ziel)` : ""}. Diese
          Kategorie wird in {place.name} nicht mehr ausgeteilt.
        </p>
      ) : null}

      {visits.length === 0 ? (
        <div className="empty">
          <h2>Keine Besuche verzeichnet</h2>
        </div>
      ) : null}

      {visits.map((visit) => {
        const played = visit.cards.filter(
          (c) => c.status === "completed" || c.status === "discarded",
        );
        const completed = played.filter(
          (c) => c.status === "completed" && c.outcome !== "failure",
        );
        const lost = played.filter((c) => c.outcome === "failure");
        return (
          <section key={visit.id} className="visit">
            <header className="visit__head">
              <h2 className="visit__date">
                {new Date(visit.started_at).toLocaleDateString("de-DE", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </h2>
              <p className="tiny muted" style={{ margin: 0 }}>
                {completed.length} {completed.length === 1 ? "Karte" : "Karten"} erledigt ·{" "}
                {visit.points} Punkte
                {lost.length > 0
                  ? ` · ${lost.length} ${lost.length === 1 ? "Wette" : "Wetten"} verloren`
                  : ""}
                {visit.ended_at ? "" : " · noch offen"}
              </p>
            </header>

            {played.length === 0 ? (
              <p className="tiny muted">Bei diesem Besuch wurde nichts erledigt.</p>
            ) : null}

            {played.map((card) => (
              <ArchiveCardBlock
                key={card.id}
                card={card}
                categoryVeto={place.category_veto}
                onOpenPhoto={setLightbox}
              />
            ))}
          </section>
        );
      })}

      {lightbox ? (
        <div className="lightbox" onClick={() => setLightbox(null)} role="dialog" aria-modal="true">
          <img src={lightbox} alt="" />
          <button className="lightbox__close" aria-label="Schließen">
            ×
          </button>
        </div>
      ) : null}
    </div>
  );
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button className="btn btn--ghost" onClick={onClick} style={{ marginLeft: -12 }}>
      ← Zurück
    </button>
  );
}

function ArchiveCardBlock({
  card,
  categoryVeto,
  onOpenPhoto,
}: {
  card: ArchiveCard;
  categoryVeto: string | null;
  onOpenPhoto: (url: string) => void;
}) {
  const photos = card.evidence.filter((e) => e.kind === "photo" && e.media_url);
  const audio = card.evidence.filter((e) => e.kind === "audio" && e.media_url);
  const notes = card.evidence.filter((e) => e.kind === "note" && e.text);
  const discarded = card.status === "discarded";
  const categoryStruck = discarded && categoryVeto === card.category;
  // A lost bet is not an abgeworfene Karte -- it was played, it just did not pay.
  const lost = card.outcome === "failure";

  return (
    <article
      className={`entry cat-${card.category} ${discarded || lost ? "entry--discarded" : ""}`}
    >
      <div className="entry__meta">
        <span className="card__category">{categoryLabel(card.category)}</span>
        <span className="card__points">{card.points} Pkt</span>
        <span style={{ marginLeft: "auto" }} className="tiny muted">
          {categoryStruck
            ? "Kategorie gestrichen"
            : discarded
              ? "Abgeworfen"
              : lost
                ? `Nicht geschafft · Ziel +${card.failure_penalty}`
                : `+${card.points_awarded} Pkt`}
        </span>
      </div>

      {/* The wording as it was that day: resolved_text is frozen at deal time, so
          this is exactly what the card said, not a re-render of the template. */}
      <p className="entry__text">{card.text}</p>

      {photos.length > 0 ? (
        <div className="entry__photos">
          {photos.map((photo) => (
            <button
              key={photo.id}
              type="button"
              onClick={() => onOpenPhoto(photo.media_url!)}
              aria-label="Foto ansehen"
            >
              <img src={photo.thumb_url ?? photo.media_url!} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      ) : null}

      {audio.map((clip) => (
        <audio key={clip.id} controls src={clip.media_url!} style={{ width: "100%", marginTop: 10 }} />
      ))}

      {notes.map((note) => (
        <blockquote key={note.id} className="entry__note">
          {note.text}
        </blockquote>
      ))}
    </article>
  );
}
